import React, { useEffect, useState } from "react";
import { firebaseApp as app } from "../firebase";
import { getFirestore, doc, getDoc, updateDoc, setDoc, query, collection, getDocs, where } from "firebase/firestore";
import { 
  Button, 
  MultiSelect, 
  Select,
  Switch,
  Stack,
  Input,
  Loader,
  Notification,
  Text,
  Card,
  SimpleGrid,
  Group,
  ActionIcon,
  ThemeIcon
} from "@mantine/core";
import { Check, Flag, Mail, BrandWhatsapp, FileDescription, Trash, Plus, AlertTriangle } from "tabler-icons-react";
import {
  isValidE164PhoneNumber,
  FlexNotificationConfig,
  FlexReportConfig,
  AnomalyDetectionConfig,
  AnomalySensitivity,
  AnomalySeverity,
  ANOMALY_SENSITIVITY_THRESHOLDS,
  DEFAULT_ANOMALY_COOLDOWN_MINUTES,
} from "@captiva/shared-types";
import { PageHeader } from "../components/shared";
import { useFeatureFlags } from "../hooks/useFeatureFlags";

const db = getFirestore(app);

export interface FlexNotificationConfigurationProps {
  companyId: string;
}

const DEFAULT_ANOMALY_CONFIG: AnomalyDetectionConfig = {
  enabled: false,
  sensitivity: "BALANCED",
  minimumSeverityToNotify: "MEDIUM",
  cooldownMinutes: DEFAULT_ANOMALY_COOLDOWN_MINUTES,
  alertOnVisionFailure: false,
};

export function FlexNotificationConfiguration({ companyId }: FlexNotificationConfigurationProps) {
  const { anomalyDetectionEnabled } = useFeatureFlags(companyId);
  const [dailyValue, setDailyValue] = useState<string[]>([]);
  const [weeklyValue, setWeeklyValue] = useState<string[]>([]);
  const [monthlyValue, setMonthlyValue] = useState<string[]>([]);

  const [notificationConfigFetched, setNotificationConfigFetched] = useState<boolean>(false);
  const [notificationConfigs, setNotificationConfigs] = useState<Partial<FlexNotificationConfig>>({});

  const [reportConfigsFetched, setReportConfigsFetched] = useState<boolean>(false);
  const [reportConfigs, setReportConfigs] = useState<FlexReportConfig[]>([]);

  const [loading, setLoading] = useState<boolean>(true);

  const [emailList, setEmailList] = useState<string[]>([]);
  const [whatsappList, setWhatsappList] = useState<string[]>([]);
  const [anomalyConfig, setAnomalyConfig] = useState<AnomalyDetectionConfig>(DEFAULT_ANOMALY_CONFIG);

  const [message, setMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      const reference = doc(db, 'flexReportNotifications', companyId);
      const notificationSnapshot = await getDoc(reference);
      if (notificationSnapshot.exists()) {
        setNotificationConfigs(notificationSnapshot.data() as FlexNotificationConfig);
      } else {
        setNotificationConfigs({ emails: [], whatsappNumbers: [], daily: [], weekly: [], monthly: [] });
      }
      setLoading(false);
    };

    try {
      if (!notificationConfigFetched && companyId) {
        setNotificationConfigFetched(true);
        fetchConfig();
      }
    } catch (error) {
      console.log("Error fetching flex-notifications configuration:", error);
    }
  }, [notificationConfigFetched, companyId]);

  useEffect(() => {
    const buildQuery = (templateName: string) => {
      return query(
        collection(db, templateName),
        where("companyId", "==", companyId),
        where("isActive", "==", true)
      );
    };
    const fetchConfigs = async () => {
      try {
        const documentSnapshots = await getDocs(buildQuery("flexReports"));
        console.log(`Fetched ${documentSnapshots.docs.length} active flexReports`);
        handleResults(documentSnapshots);
      } catch (error) {
        console.error("Error fetching flexReports for dropdowns:", error);
      }
    };
    const handleResults = (documentSnapshots: any) => {
      let entries: FlexReportConfig[] = [];
      documentSnapshots.forEach((docSnap: any) => {
        entries.push({ id: docSnap.id, ...docSnap.data() } as FlexReportConfig);
      });
      console.log("Parsed flexReports entries:", entries);
      sortByName(entries);
      setReportConfigs(entries);
    };
    try {
      if (!reportConfigsFetched && companyId) {
        setReportConfigsFetched(true);
        fetchConfigs();
      }
    } catch (error) {
      console.log(error);
    }
  }, [reportConfigsFetched, companyId]);

  useEffect(() => {
    setEmailList(notificationConfigs.emails || []);
    setWhatsappList(notificationConfigs.whatsappNumbers || []);
    setDailyValue(notificationConfigs.daily || []);
    setWeeklyValue(notificationConfigs.weekly || []);
    setMonthlyValue(notificationConfigs.monthly || []);
    if (notificationConfigs.anomalyConfig) {
      setAnomalyConfig({
        ...DEFAULT_ANOMALY_CONFIG,
        ...notificationConfigs.anomalyConfig,
      });
    } else {
      setAnomalyConfig(DEFAULT_ANOMALY_CONFIG);
    }
  }, [notificationConfigs]);

  const removeEmail = (index: number) => {
    const newEmails = [...emailList];
    newEmails.splice(index, 1);
    setEmailList(newEmails);
  };

  const removeWhatsapp = (index: number) => {
    const newList = [...whatsappList];
    newList.splice(index, 1);
    setWhatsappList(newList);
  };

  const saveConfig = async () => {
    setMessage("");
    setErrorMessage("");

    const validWhatsappNumbers = whatsappList.filter(w => !!w);
    for (const number of validWhatsappNumbers) {
      if (!isValidE164PhoneNumber(number)) {
        setErrorMessage(`Invalid WhatsApp number: ${number}. Must be in E.164 format (e.g. +27821234567)`);
        return;
      }
    }

    const payload: Record<string, unknown> = {
      companyId: companyId,
      daily: dailyValue,
      weekly: weeklyValue,
      monthly: monthlyValue,
      emails: emailList.filter(e => !!e),
      whatsappNumbers: validWhatsappNumbers,
      hasDaily: dailyValue.length > 0,
      hasWeekly: weeklyValue.length > 0,
      hasMonthly: monthlyValue.length > 0,
    };
    if (anomalyDetectionEnabled) {
      payload.anomalyConfig = anomalyConfig;
    }
    const reference = doc(db, 'flexReportNotifications', companyId);

    try {
      const snap = await getDoc(reference);
      if (snap.exists()) {
        await updateDoc(reference, payload);
      } else {
        await setDoc(reference, payload);
      }
      setNotificationConfigFetched(false);
      setMessage("Configuration successfully updated.");
    } catch (e: any) {
      setErrorMessage(`An error occurred: ${e.message}`);
    }
  };

  const sortByName = (entries: FlexReportConfig[]) => {
    entries.sort((a, b) => {
      const nameA = a.name || "";
      const nameB = b.name || "";
      return nameA.localeCompare(nameB);
    });
  };

  return (
    <>
      <PageHeader
        title="Flex Alerts & Notifications"
        subtitle="Configure automated schedule and alert recipients for Flex modules"
        alert={
          (message || errorMessage) ? (
            <>
              {message && (
                <Notification
                  icon={<Check size={18} />}
                  color="teal"
                  title="Success"
                  onClose={() => setMessage("")}
                  style={{ marginBottom: errorMessage ? "0.5em" : 0 }}
                >
                  {message}
                </Notification>
              )}
              {errorMessage && (
                <Notification
                  icon={<Flag size={18} />}
                  color="red"
                  title="Error"
                  onClose={() => setErrorMessage("")}
                >
                  {errorMessage}
                </Notification>
              )}
            </>
          ) : undefined
        }
      />
      {loading ? (
        <Loader style={{ margin: "2em auto", display: "block" }} />
      ) : (
        <div style={{ padding: "0.5em 0", maxWidth: "800px" }}>
          <Card withBorder shadow="sm" radius="md" mb="md">
            <Group mb="sm">
              <ThemeIcon size="md" variant="light" color="blue">
                <Mail size={16} />
              </ThemeIcon>
              <Text size="md" weight={400}>Emails</Text>
            </Group>
            
            <Stack spacing="sm">
              {emailList.map((email, index) => (
                <Group key={`email-${index}`} noWrap>
                  <Input 
                    value={email}
                    size="md"
                    placeholder="email@example.com"
                    style={{ flex: 1 }}
                    onChange={(event) => {
                      const newEmails = [...emailList];
                      newEmails[index] = event.currentTarget.value;
                      setEmailList(newEmails);
                    }}
                  />
                  <ActionIcon color="red" variant="transparent" size="lg" onClick={() => removeEmail(index)} style={{ border: "none", backgroundColor: "transparent" }}>
                    <Trash size={20} color="#fa5252" />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
            <Button 
              mt="md" 
              variant="subtle" 
              color="blue" 
              leftIcon={<Plus size={16} />} 
              onClick={() => setEmailList([...emailList, ""])}
            >
              Add Email Address
            </Button>
          </Card>

          <Card withBorder shadow="sm" radius="md" mb="md">
            <Group mb="sm">
              <ThemeIcon size="md" variant="light" color="teal">
                <BrandWhatsapp size={16} />
              </ThemeIcon>
              <Text size="md" weight={400}>WhatsApp Numbers</Text>
            </Group>

            <Stack spacing="sm">
              {whatsappList.map((number, index) => (
                <Group key={`wa-${index}`} noWrap>
                  <Input
                    value={number}
                    size="md"
                    placeholder="+27821234567"
                    style={{ flex: 1 }}
                    onChange={(event) => {
                      const newList = [...whatsappList];
                      newList[index] = event.currentTarget.value;
                      setWhatsappList(newList);
                    }}
                  />
                  <ActionIcon color="red" variant="transparent" size="lg" onClick={() => removeWhatsapp(index)} style={{ border: "none", backgroundColor: "transparent" }}>
                    <Trash size={20} color="#fa5252" />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
            <Button
              mt="md"
              variant="subtle"
              color="teal"
              leftIcon={<Plus size={16} />}
              onClick={() => setWhatsappList([...whatsappList, ""])}
            >
              Add WhatsApp Number
            </Button>
          </Card>

          <Card withBorder shadow="sm" radius="md" mb="md">
            <Group mb="sm">
              <ThemeIcon size="md" variant="light" color="grape">
                <FileDescription size={16} />
              </ThemeIcon>
              <Text size="md" weight={400}>Report Triggers</Text>
            </Group>

            <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]} spacing="sm">
              <MultiSelect 
                label="Daily reports"
                placeholder="Select reports"
                searchable
                clearable
                nothingFound="No matches found"
                value={dailyValue} 
                onChange={setDailyValue} 
                data={reportConfigs.map((config) => {
                  return { value: config.id || "", label: config.name || "Unnamed Report" };
                })} 
              />
              <MultiSelect 
                label="Weekly reports"
                placeholder="Select reports"
                searchable
                clearable
                nothingFound="No matches found"
                value={weeklyValue}
                onChange={setWeeklyValue} 
                data={reportConfigs.map((config) => {
                  return { value: config.id || "", label: config.name || "Unnamed Report" };
                })} 
              />
              <MultiSelect 
                label="Monthly reports"
                placeholder="Select reports"
                searchable
                clearable
                nothingFound="No matches found"
                value={monthlyValue} 
                onChange={setMonthlyValue} 
                data={reportConfigs.map((config) => {
                  return { value: config.id || "", label: config.name || "Unnamed Report" };
                })} 
              />
            </SimpleGrid>
          </Card>

          {anomalyDetectionEnabled && (
            <Card withBorder shadow="sm" radius="md" mb="md">
              <Group position="apart" mb="sm">
                <Group>
                  <ThemeIcon size="md" variant="light" color="orange">
                    <AlertTriangle size={16} />
                  </ThemeIcon>
                  <div>
                    <Text size="md" weight={500}>Real-Time AI Anomaly &amp; Spike Alerts</Text>
                    <Text size="xs" color="dimmed">
                      Automatically evaluate Flex submissions (e.g. pesticide/fertiliser rate per hectare, water flow meter deltas, equipment checklists, and Option Set rate bounds)
                    </Text>
                  </div>
                </Group>
                <Switch
                  checked={anomalyConfig.enabled}
                  onChange={(event) =>
                    setAnomalyConfig({
                      ...anomalyConfig,
                      enabled: event.currentTarget.checked,
                    })
                  }
                  label={anomalyConfig.enabled ? "Enabled" : "Disabled"}
                  color="orange"
                />
              </Group>

              {anomalyConfig.enabled && (
                <Stack spacing="sm" mt="md">
                  <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]} spacing="sm">
                    <Select
                      label="Detection Sensitivity"
                      description={ANOMALY_SENSITIVITY_THRESHOLDS[anomalyConfig.sensitivity || "BALANCED"]?.description}
                      value={anomalyConfig.sensitivity || "BALANCED"}
                      onChange={(val) =>
                        val &&
                        setAnomalyConfig({
                          ...anomalyConfig,
                          sensitivity: val as AnomalySensitivity,
                        })
                      }
                      data={[
                        { value: "CONSERVATIVE", label: "Conservative (2.5x baseline / 3.5σ)" },
                        { value: "BALANCED", label: "Balanced — Recommended (2.0x baseline / 3.0σ)" },
                        { value: "SENSITIVE", label: "Sensitive (1.6x baseline / 2.5σ)" },
                      ]}
                    />
                    <Select
                      label="Minimum Alert Severity"
                      description="Only dispatch Email & WhatsApp alerts at or above this severity"
                      value={anomalyConfig.minimumSeverityToNotify || "MEDIUM"}
                      onChange={(val) =>
                        val &&
                        setAnomalyConfig({
                          ...anomalyConfig,
                          minimumSeverityToNotify: val as Exclude<AnomalySeverity, "NONE">,
                        })
                      }
                      data={[
                        { value: "MEDIUM", label: "Medium & Above (2.0x+ spikes & rate exceedances)" },
                        { value: "HIGH", label: "High & Critical Only (major spikes & 7-day surges)" },
                        { value: "CRITICAL", label: "Critical Only (severe overdosing / hazard)" },
                      ]}
                    />
                  </SimpleGrid>

                  <Switch
                    mt="xs"
                    checked={!!anomalyConfig.alertOnVisionFailure}
                    onChange={(event) =>
                      setAnomalyConfig({
                        ...anomalyConfig,
                        alertOnVisionFailure: event.currentTarget.checked,
                      })
                    }
                    label="Also trigger an anomaly evaluation when AI Photo Verification detects a meter or image mismatch"
                  />
                </Stack>
              )}
            </Card>
          )}

          <div style={{ position: 'sticky', bottom: 0, padding: '1rem 0', backgroundColor: 'var(--mantine-color-body, white)', borderTop: '1px solid #eaeaea', zIndex: 10, marginTop: 'auto' }}>
            <Button size="md" color="cap-navy" onClick={saveConfig} style={{ minWidth: 150 }}>
              Save
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
export default FlexNotificationConfiguration;
